import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as client from "../api/client";

export interface AuthUser {
  sub: string;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
}

interface AuthActions {
  login: (user: AuthUser) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      user: null,
      isAuthenticated: false,
      loading: true,

      login: (userData: AuthUser) => {
        set(
          { user: userData, isAuthenticated: true, loading: false },
          false,
          "auth/login",
        );
      },

      logout: async () => {
        try {
          await client.logout();
        } catch {
          // Clear state even if server call fails
        }
        set({ user: null, isAuthenticated: false }, false, "auth/logout");
        localStorage.clear();
        sessionStorage.clear();
      },

      checkAuth: async () => {
        set({ loading: true }, false, "auth/checkAuth/start");
        try {
          const response = await client.getAuthStatus();
          if (response.authenticated && response.user) {
            set(
              {
                user: response.user,
                isAuthenticated: true,
                loading: false,
              },
              false,
              "auth/checkAuth/success",
            );
            return true;
          }
        } catch {
          // Not authenticated
        }
        set(
          { user: null, isAuthenticated: false, loading: false },
          false,
          "auth/checkAuth/fail",
        );
        return false;
      },
    }),
    { name: "AuthStore" },
  ),
);
